import 'source-map-support/register'
import './web/styles/index.scss'

import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { App } from 'web/App'
import { getCurrentBuild } from 'web/data/core-data'
import { ErrorBoundary } from 'web/ErrorBoundary'
import { ErrorView } from 'web/ErrorView'
import { DisplayFightActions } from 'web/redux/slices/display-fight-slice'
import { MetaActions } from 'web/redux/slices/meta-slice'
import { ModalActions } from 'web/redux/slices/modal-slice'
import { RosterActions } from 'web/redux/slices/roster-slice'
import { store } from 'web/redux/store'
import { assertAndCastBoolean, assertDefined, expectOneById, getQueryParamValue, joinLines, tryGetOneById } from 'web/utils/utils'
import { AppActions, RenderMode } from 'web/redux/slices/app-slice'
import { EditFightActions } from 'web/redux/slices/edit-fight-slice'
import { EditSetupActions } from 'web/redux/slices/edit-setup-slice'
import { SetupActions } from 'web/redux/slices/setup-slice'

checkBuildVersion_1()
firstRun_2()
attemptSetupRecovery_3()
dataCleanup_4()

    ReactDOM.render(
        <Provider store={store}>
            <ErrorBoundary>
                <React.StrictMode>
                    <App />
                </React.StrictMode>
            </ErrorBoundary>
        </Provider>,
        document.getElementById('root')
    )

/**
 * Checks if a newer build is available and updates the application to
 * use it.
 */
function checkBuildVersion_1() {

    const { lastKnownPatchInfo } = store.getState().meta
    const currentBuild = getCurrentBuild()

    function isCurrOlderThanLastKnown(lastKnownVer: string, currVer: string) {
        /*
            I fucked up here. The version names are strings with
            underscores, so when we do a less-than comparison we don't
            get the expected numerical sort.

            This function takes care of unfucking that mistake for
            now.
        */

        const verA = parseInt(lastKnownVer.replace("_", ""))
        const verB = parseInt(currVer.replace("_", ""))

        return verA > verB
    }

    if (isCurrOlderThanLastKnown(lastKnownPatchInfo.buildVersion, currentBuild.patchInfo.buildVersion)) {
        // TODO: How do I contact a dev?
        const message = joinLines([
            'The current build is older than your previously known build,',
            'something is very wrong. Contact the dev for help with troubleshooting.',
        ])

        ReactDOM.render(
            <ErrorView message={message} />,
            document.getElementById('root')
        )

        throw Error(message)
    }

    const forceShowModal = assertAndCastBoolean(getQueryParamValue({
        query: window.location.search,
        paramName: "DEV:SHOW_BUILD_INFO"
    }))

    if (lastKnownPatchInfo.buildVersion !== currentBuild.patchInfo.buildVersion) {

        store.dispatch(RosterActions.prunePlayers())
        store.dispatch(ModalActions.openNotifyAboutNewBuildModal())
        store.dispatch(MetaActions.setLastKnownPatchInfo())

        // We're not going to attempt setup recovery if the build
        // changed, just flush it, it's safer than attempting a
        // recovery anyway.
        store.dispatch(MetaActions.clearSetupRecovery())

    } else if (forceShowModal === true) {

        store.dispatch(ModalActions.openNotifyAboutNewBuildModal())

    }

}

/**
 * Ensure the app is ready to be ran for the first time.
 */
function firstRun_2() {
    const { fight, setup, meta } = store.getState()

    // Ensure we have a selected fight on the initial run no matter what
    store.dispatch(DisplayFightActions.selectFight({ fightId: fight.fights[0].id }))

    // Check if there's any previous session settings saved
    const { lastSelectedFightId, lastSelectedSetupId } = meta

    if (lastSelectedFightId != null) {
        if (tryGetOneById(fight.fights, lastSelectedFightId)) {
            store.dispatch(DisplayFightActions.selectFight({ fightId: lastSelectedFightId }))
        } else {
            store.dispatch(MetaActions.clearLastSelectedFightAndSetup())
        }
    }

    if (lastSelectedSetupId != null) {
        if (tryGetOneById(setup.setups, lastSelectedSetupId)) {
            store.dispatch(DisplayFightActions.selectSetup({ selectedSetupId: lastSelectedSetupId }))
        } else {
            store.dispatch(MetaActions.clearLastSelectedSetup())
        }
    }
}

/**
 * Check if there's recovery data and proceed to recover it.
 */
function attemptSetupRecovery_3() {

    const { meta, setup, fight } = store.getState()

    if (meta.setupRecovery) {

        const affectedSetup = expectOneById(setup.setups, meta.setupRecovery.setupId)
        const affectedFight = expectOneById(fight.fights, meta.setupRecovery.fightId)

        store.dispatch(ModalActions.openConfirmModal({
            confirmText: 'Restore',
            btnColor: 'success',
            title: 'Restore unsaved setup changes',
            content: (
                <div>
                    <p>
                        {`Detected unsaved setup changes from your previous session. Do you want to recover them or discard them forever?`}
                    </p>
                    <ul className="mt-2">
                        <li>
                            {`Fight: ${affectedFight.name}`}
                        </li>
                        <li>
                            {`Setup: ${affectedSetup.name}`}
                        </li>
                    </ul>
                    <p>
                        {`Note, if you choose to recover them, you can still cancel all the changes after reviewing them.`}
                    </p>
                    <p className="m-0 p-0">
                        {`Pressing "Restore" will take you directly the the normal edit-mode.`}
                    </p>
                </div>
            ),
            onConfirm() {
                assertDefined(meta.setupRecovery)

                store.dispatch(EditSetupActions.loadRecovery({
                    recoveryJson: meta.setupRecovery.editSetupJson
                }))

                store.dispatch(EditFightActions.loadRecovery({
                    recoveryJson: meta.setupRecovery.editFightJson
                }))

                store.dispatch(AppActions.setRenderMode({ renderMode: RenderMode.EditAssignments }))
            },
            onClose() {
                store.dispatch(MetaActions.clearSetupRecovery())
            },
        }))

    }

}

function dataCleanup_4() {
    /*
        Here you implement any data-cleanup you need to perform. You
        MUST implement the cleanup logic as redux actions. This
        ensures that data is stored via a middleware that intercepts
        all actions and writes to disk.
    */

    store.dispatch(SetupActions.cleanupDeprecated_fromRosterPlayerId_field())
}
